// Puerto verbatim de packages/hce/src/hce/paciente/historiaClinica.ts.
import type { MedplumClient } from '@medplum/core';
import type { ExtractResource, ResourceType } from '@medplum/fhirtypes';
import { CATEGORIA_PROCEDURE_120, CODIGO_NOTA_SOAPIE, SISTEMA_NOTA_TURNO } from '../formulario/discretos/enfermeria';

/**
 * Cada búsqueda se resuelve por separado (nunca un solo `Promise.all` para
 * las 8): un AccessPolicy que a un rol le falte por completo — un caso real,
 * ver el bloqueante del revisor de permisos 2026-09-14 sobre Psicólogo/
 * Odontólogo sin `Procedure` — no debe tumbar TODA la pestaña "Historia"
 * (encounters, diagnósticos, recetas incluidos) por un 403 en una sola
 * búsqueda. Sin dato para esa sección en vez de sin pestaña.
 */
async function buscarOVacio<RT extends ResourceType>(medplum: MedplumClient, resourceType: RT, query: Record<string, string>): Promise<ExtractResource<RT>[]> {
  try {
    return await medplum.searchResources(resourceType, query);
  } catch (err) {
    console.error(`[historiaClinica] ${resourceType}: ${err instanceof Error ? err.message : String(err)}`);
    return [];
  }
}

/**
 * Lo que documenta Enfermería (Kardex 022, Intervenciones 120, Nota de turno
 * SOAPIE) — pedido de Sue 2026-09-14: hasta acá un médico no tenía forma de
 * enterarse de esto en la ficha del paciente (ni pestaña propia, ni acá).
 * "Médico"/Psicólogo/Odontólogo ya tienen permiso real para leer estos tres
 * recursos (ver deploy/medplum/plantillas/access-policies/), así que sumarlos
 * acá es solo agregarlos a la cronología existente.
 */
export async function traerHistoriaClinica(medplum: MedplumClient, patientRef: string) {
  const query = { patient: patientRef, _count: '200', _sort: '-date' };
  const categoriaProcedure120 = `${CATEGORIA_PROCEDURE_120.coding?.[0]?.system}|${CATEGORIA_PROCEDURE_120.coding?.[0]?.code}`;
  const [encounters, questionnaireResponses, observations, conditions, medicationRequests, intervencionesEnfermeria, notasTurnoEnfermeria, administracionesEnfermeria] = await Promise.all([
    buscarOVacio(medplum, 'Encounter', { patient: patientRef, _count: '200' }),
    buscarOVacio(medplum, 'QuestionnaireResponse', { patient: patientRef, _count: '200' }),
    buscarOVacio(medplum, 'Observation', query),
    buscarOVacio(medplum, 'Condition', { patient: patientRef, _count: '200' }),
    buscarOVacio(medplum, 'MedicationRequest', { patient: patientRef, _count: '200' }),
    buscarOVacio(medplum, 'Procedure', { patient: patientRef, category: categoriaProcedure120, _count: '200' }),
    buscarOVacio(medplum, 'Composition', { patient: patientRef, type: `${SISTEMA_NOTA_TURNO}|${CODIGO_NOTA_SOAPIE}`, _count: '200' }),
    buscarOVacio(medplum, 'MedicationAdministration', { patient: patientRef, _count: '200' }),
  ]);
  return { encounters, questionnaireResponses, observations, conditions, medicationRequests, intervencionesEnfermeria, notasTurnoEnfermeria, administracionesEnfermeria };
}

export type HistoriaClinica = Awaited<ReturnType<typeof traerHistoriaClinica>>;
